const COMPLEX_CATEGORY_TOKENS = new Set([
  "CBTL",
  "COMPLEX BTL",
  "COMPLEX-BTL",
  "COMPLEX_BTL",
]);

const CATEGORY_FIELDS = [
  "product_category",
  "category",
  "productCategory",
];

const ID_FIELDS = [
  "name",
  "id",
  "_id",
  "mortgage_product",
  "product_id",
  "productId",
  "product",
];

export const PROPERTY_TYPE_CHIPS = [
  { key: "BTL", label: "BTL", tone: "border-sky-200 bg-sky-50 text-sky-700" },
  { key: "HMO", label: "HMO", tone: "border-violet-200 bg-violet-50 text-violet-700" },
  { key: "MUFB", label: "MUFB", tone: "border-amber-200 bg-amber-50 text-amber-700" },
  { key: "Short term let", label: "STL", tone: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  { key: "Holiday let", label: "Holiday let", tone: "border-teal-200 bg-teal-50 text-teal-700" },
  { key: "MPOT", label: "MPOT", tone: "border-orange-200 bg-orange-50 text-orange-700" },
  { key: "Portfolio", label: "Portfolio", tone: "border-rose-200 bg-rose-50 text-rose-700" },
];

const PROPERTY_FIELD_KEYS = [
  "hmo_max_rooms",
  "mufb_max_units",
  "mufb_houses",
  "mufb_flats",
  "mufb_units_retained_percent",
  "short_term_lets_avg_weeks",
  "short_term_lets_standard_rental_income",
  "holiday_lets_avg_weeks",
];

function asUpper(value) {
  return String(value || "").trim().toUpperCase();
}

function safeNum(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function fieldIsSet(value) {
  return (
    safeNum(value) > 0 ||
    value === true ||
    /^(yes|y|true)$/i.test(String(value ?? "").trim())
  );
}

function anyPositive(product, keys) {
  return keys.some((key) => fieldIsSet(product?.[key]));
}

function anyWithPrefix(product, prefix) {
  return Object.keys(product || {}).some(
    (key) => key.startsWith(prefix) && fieldIsSet(product[key])
  );
}

function flattenProductSources(source = {}) {
  if (!source || typeof source !== "object") return [];
  return [
    source,
    source._raw,
    source.fullApp,
    source.product && typeof source.product === "object" ? source.product : null,
    source.applicationPayload,
    source.application_payload && typeof source.application_payload === "object"
      ? source.application_payload
      : null,
  ].filter(Boolean);
}

export function getProductCategory(source = {}) {
  if (typeof source === "string") return asUpper(source);
  for (const item of flattenProductSources(source)) {
    for (const field of CATEGORY_FIELDS) {
      const value = asUpper(item?.[field]);
      if (value) return value;
    }
  }
  return "";
}

function collectProductIds(source = {}) {
  const ids = [];
  const items = typeof source === "object" ? flattenProductSources(source) : [];
  for (const item of items) {
    for (const field of ID_FIELDS) {
      const value = asUpper(item?.[field]);
      if (value) ids.push(value);
    }
  }
  return ids;
}

function categoryLooksComplex(value) {
  const cat = asUpper(value);
  if (!cat) return false;
  return COMPLEX_CATEGORY_TOKENS.has(cat) || cat.includes("COMPLEX");
}

export function isComplexBtlProduct(source = {}) {
  if (source == null) return false;
  if (typeof source !== "object") return categoryLooksComplex(source);

  if (categoryLooksComplex(getProductCategory(source))) return true;

  const typeFields = [
    source.productType,
    source.product_type,
    source.loan_type,
    source._raw?.productType,
    source._raw?.product_type,
    source.applicationPayload?.productType,
    source.applicationPayload?.product_category,
  ];
  if (typeFields.some((value) => categoryLooksComplex(value))) return true;

  return collectProductIds(source).some((id) => id.startsWith("BTX-"));
}

function categoryLooksIf(value) {
  const cat = asUpper(value);
  return cat === "IF" || cat.includes("INVOICE");
}

export function resolveBorrowerProductCategory(source = {}) {
  if (source == null) return "BTL";

  if (typeof source !== "object") {
    const value = asUpper(source);
    if (categoryLooksIf(value)) return "IF";
    if (categoryLooksComplex(value)) return "CBTL";
    if (value === "ASSET" || value === "AF" || value.includes("ASSET")) return "ASSET";
    if (value === "COMMERCIAL" || value.includes("COMMERCIAL")) return "COMMERCIAL";
    if (value === "BTL" || value.includes("BUY-TO-LET") || value.includes("BUY TO LET")) return "BTL";
    return value || "BTL";
  }

  const cat = getProductCategory(source);
  const typeHint = asUpper(
    source.productType ||
      source.product_type ||
      source.loan_type ||
      source.applicationPayload?.productType ||
      ""
  );

  if (categoryLooksIf(cat) || categoryLooksIf(typeHint)) return "IF";
  if (isComplexBtlProduct(source)) return "CBTL";
  if (cat === "ASSET" || cat === "AF" || cat.includes("ASSET") || typeHint === "ASSET" || typeHint === "AF") {
    return "ASSET";
  }
  if (cat === "COMMERCIAL" || cat.includes("COMMERCIAL") || typeHint === "COMMERCIAL") {
    return "COMMERCIAL";
  }
  if (cat === "BTL" || cat.includes("BUY-TO-LET") || cat.includes("BUY TO LET")) return "BTL";
  if (["BTL", "IF", "ASSET", "COMMERCIAL"].includes(typeHint)) return typeHint;
  return "BTL";
}

export function formatProductCategoryLabel(category) {
  const cat = asUpper(category);
  if (categoryLooksComplex(cat) || cat === "CBTL") return "Complex BTL";
  if (categoryLooksIf(cat)) return "Invoice Finance";
  if (cat === "ASSET" || cat === "AF") return "Asset Finance";
  if (cat === "COMMERCIAL") return "Commercial";
  if (cat === "BTL") return "BTL";
  return String(category || "—").trim() || "—";
}

export function isBtlLikeProductType(productType) {
  const cat = asUpper(productType);
  return (
    cat === "BTL" ||
    cat === "CBTL" ||
    cat.includes("COMPLEX") ||
    cat.includes("BUY-TO-LET") ||
    cat.includes("BUY TO LET")
  );
}

export function productMatchesPropertyType(product, type) {
  const src = product?._raw ? { ...product._raw, ...product } : product || {};
  switch (type) {
    case "BTL":
      return getProductCategory(src) === "BTL" && !isComplexBtlProduct(src);
    case "HMO":
      return anyPositive(src, ["hmo_max_rooms"]);
    case "MUFB":
      return anyPositive(src, [
        "mufb_max_units",
        "mufb_houses",
        "mufb_flats",
        "mufb_units_retained_percent",
      ]);
    case "Short term let":
      return anyPositive(src, [
        "short_term_lets_avg_weeks",
        "short_term_lets_standard_rental_income",
      ]);
    case "Holiday let":
      return anyPositive(src, ["holiday_lets_avg_weeks"]);
    case "MPOT":
      return anyWithPrefix(src, "mpot_");
    case "Portfolio":
      return anyWithPrefix(src, "portfolio_");
    default:
      return false;
  }
}

export function productPropertyTypeChips(product) {
  const chips = PROPERTY_TYPE_CHIPS.filter((chip) =>
    productMatchesPropertyType(product, chip.key)
  );
  if (isComplexBtlProduct(product)) {
    return chips.filter((chip) => chip.key !== "BTL");
  }
  return chips;
}

export function pickComplexBtlPropertyFields(product = {}) {
  const src = product?._raw ? { ...product._raw, ...product } : product || {};
  const out = {};
  for (const key of PROPERTY_FIELD_KEYS) {
    if (src[key] != null && src[key] !== "") out[key] = src[key];
  }
  for (const key of Object.keys(src)) {
    if ((key.startsWith("mpot_") || key.startsWith("portfolio_")) && src[key] != null && src[key] !== "") {
      out[key] = src[key];
    }
  }
  return out;
}
